import React, { useState } from "react";
import { Link } from "react-router-dom";
import { FiMenu, FiX, FiArrowRight } from "react-icons/fi";

interface LandingHeaderProps {
  scrollTo: (id: string) => void;
}

export const LandingHeader: React.FC<LandingHeaderProps> = ({ scrollTo }) => {
  const [menuOpen, setMenuOpen] = useState(false);

  const toggleMenu = () => {
    setMenuOpen(!menuOpen);
  };

  const handleNavClick = (id: string) => {
    setMenuOpen(false);
    scrollTo(id);
  };

  return (
    <header className="sticky top-0 z-50 bg-white/90 backdrop-blur-md border-b border-slate-100 shadow-sm transition-all">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-20 flex items-center justify-between">
        
        {/* Brand Logo */}
        <Link to="/landingpage" className="flex items-center gap-3 group">
          <img
            src="/1.png"
            alt="Pendulum Digital"
            className="h-12 w-auto object-contain transition-transform group-hover:scale-105"
            onError={(e) => {
              // Fallback text if logo image is missing
              (e.target as HTMLElement).style.display = "none";
            }}
          />
          <div className="hidden sm:flex flex-col">
            <span className="font-black text-slate-900 text-lg tracking-tight leading-none">
              PENDULUM
            </span>
            <span className="text-[10px] font-black uppercase tracking-widest text-blue-600">
              Digital Learning
            </span>
          </div>
        </Link>

        {/* Desktop Navigation Links */}
        <nav className="hidden md:flex items-center gap-1 bg-slate-50/80 p-1.5 rounded-full border border-slate-200/60 shadow-inner">
          <button
            onClick={() => handleNavClick("about")}
            className="px-5 py-2 text-xs font-bold text-slate-600 hover:text-blue-600 hover:bg-white rounded-full transition-all"
          >
            About Us
          </button>
          <button
            onClick={() => handleNavClick("how-it-works")}
            className="px-5 py-2 text-xs font-bold text-slate-600 hover:text-blue-600 hover:bg-white rounded-full transition-all"
          >
            How it Works
          </button>
          <button
            onClick={() => handleNavClick("newsletter")}
            className="px-5 py-2 text-xs font-bold text-slate-600 hover:text-blue-600 hover:bg-white rounded-full transition-all"
          >
            Newsletter
          </button>
          <button
            onClick={() => handleNavClick("FAQs")}
            className="px-5 py-2 text-xs font-bold text-slate-600 hover:text-blue-600 hover:bg-white rounded-full transition-all"
          >
            FAQs
          </button>
        </nav>

        {/* Action Buttons */}
        <div className="hidden md:flex items-center gap-3">
          <Link
            to="/login"
            className="px-5 py-2.5 rounded-xl text-xs font-black text-slate-700 hover:text-blue-600 hover:bg-slate-50 transition-all border border-slate-200/80"
          >
            Sign In
          </Link>
          <Link
            to="/register"
            className="px-6 py-2.5 rounded-xl text-xs font-black text-white bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 shadow-md shadow-blue-500/20 hover:shadow-lg transition-all flex items-center gap-1.5"
          >
            Register School <FiArrowRight size={14} />
          </Link>
        </div>

        {/* Mobile Hamburger Button */}
        <div className="md:hidden flex items-center gap-2">
          <button
            onClick={toggleMenu}
            className="p-2.5 rounded-xl bg-slate-100 text-slate-700 hover:bg-slate-200 transition-colors"
            aria-label="Toggle menu"
          >
            {menuOpen ? <FiX size={22} /> : <FiMenu size={22} />}
          </button>
        </div>
      </div>

      {/* Mobile Drawer Menu */}
      {menuOpen && (
        <div className="md:hidden bg-white border-b border-slate-200 px-6 py-6 space-y-4 animate-in slide-in-from-top duration-200 shadow-xl">
          <nav className="flex flex-col space-y-2">
            <button
              onClick={() => handleNavClick("about")}
              className="text-left py-2.5 px-4 text-sm font-bold text-slate-700 hover:bg-blue-50 hover:text-blue-600 rounded-xl transition-all"
            >
              About Us
            </button>
            <button
              onClick={() => handleNavClick("how-it-works")}
              className="text-left py-2.5 px-4 text-sm font-bold text-slate-700 hover:bg-blue-50 hover:text-blue-600 rounded-xl transition-all"
            >
              How it Works
            </button>
            <button
              onClick={() => handleNavClick("newsletter")}
              className="text-left py-2.5 px-4 text-sm font-bold text-slate-700 hover:bg-blue-50 hover:text-blue-600 rounded-xl transition-all"
            >
              Newsletter
            </button>
            <button
              onClick={() => handleNavClick("FAQs")}
              className="text-left py-2.5 px-4 text-sm font-bold text-slate-700 hover:bg-blue-50 hover:text-blue-600 rounded-xl transition-all"
            >
              FAQs
            </button>
          </nav>

          <div className="pt-4 border-t border-slate-100 flex flex-col gap-2">
            <Link
              to="/login"
              onClick={() => setMenuOpen(false)}
              className="w-full text-center py-3 rounded-xl text-xs font-black text-slate-700 bg-slate-100 hover:bg-slate-200 transition-all"
            >
              Sign In
            </Link>
            <Link
              to="/register"
              onClick={() => setMenuOpen(false)}
              className="w-full text-center py-3 rounded-xl text-xs font-black text-white bg-blue-600 hover:bg-blue-700 transition-all shadow-md"
            >
              Register School
            </Link>
          </div>
        </div>
      )}
    </header>
  );
};

export default LandingHeader;
